import type { FC, PropsWithChildren } from 'react';
import { memo, useCallback } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import casinoConfig from '@dcentralexpedientedigital/config';
import { useIntlStore } from '@dcentralexpedientedigital/fe-shared/stores/intl-store';
import { useShallow } from 'zustand/react/shallow';

import { useHtmlLang } from '../hooks/use-html-lang';
import { useUpdateTranslations } from '../hooks/use-update-translations';
import { isValidLocale } from '../utils/language';

const DEFAULT_LOCALE = casinoConfig.defaultLanguage.toLowerCase();

export const IntlProvider: FC<PropsWithChildren> = memo(({ children }) => {
    useUpdateTranslations();
    useHtmlLang();

    const { translations, locale } = useIntlStore(
        useShallow((state) => ({
            translations: {
                ...state.translations[DEFAULT_LOCALE],
                ...state.translations[state.locale],
            },
            locale: state.locale,
        })),
    );

    // todo:
    const onError = useCallback((err: { code: string; message: string }) => {
        if (err.code === 'MISSING_TRANSLATION') {
            // ignore: console.warn('Missing translation', err.message);
            return;
        }
        throw err;
    }, []);

    // prevent from breaking if messing around with locale in storage
    const validatedLocale = isValidLocale(locale) ? locale : DEFAULT_LOCALE;

    return (
        <ReactIntlProvider
            messages={translations}
            locale={validatedLocale}
            defaultLocale={DEFAULT_LOCALE}
            onError={onError}
        >
            {children}
        </ReactIntlProvider>
    );
});
